import { useEffect, useContext, useState, useRef } from "react";
import { ListItem, ListItemButton, ListItemText, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUIList from "../../../Component/UI/MUIList/MUIList";
import { DiscoveryContext } from "../discovery-context";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import MUIToolTip from "../../../Component/UI/MUIToolTip/MUIToolTip";
import LoadingSkeleton from "./LoadingSkeleton";
import classes from "./Attributes.module.css";
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";
import { AuthContext } from "../../../context/auth-context";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

export default function Attributes({ entity, market }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const discoveryType = discoveryCtx.type || "brand";
  const llm = discoveryCtx.llm;
  let positiveAttributes = [];
  let negativeAttributes = [];
  let loading = true;
  let brandData = discoveryCtx.positive_negative_attributes || [];
  let hasError = false;
  const { user } = useContext(AuthContext);
  const transactionStatus = discoveryCtx.state.transactionStatus;
  const baseResponsesData = brandData[0]?.baseResponses || {};
  const iswebSearchLLM = llm?.toLowerCase()?.includes("search");
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    closeInfoModal,
    handleSignalStrengthClick,
  } = useBaseResponsesModal({ baseResponses: baseResponsesData }, "signal-strength");

  const cardTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <span>{discoveryType === "brand" ? ENTITY_LABELS.brandAttributes : ENTITY_LABELS.productAttributes}</span>
      {!isCompanyUser && Object.keys(baseResponsesData)?.length >0 && (
        <Tooltip title="View Base Prompt" arrow placement="top">
          <IconButton
            size="small"
            onClick={() => handleSignalStrengthClick(iswebSearchLLM, baseResponsesData,discoveryType)}
            sx={{
              padding: "2px",
              color: "rgba(0, 0, 0, 0.54)",
              "&:hover": {
                backgroundColor: "rgba(33, 150, 243, 0.08)",
                color: "#2196F3",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  const renderRow = (item, index) => {
    const isEven = index % 2 === 0;
    const isFirst = index === 0;

    const style = {
      background: isEven ? "#F8FAFB" : "#FFFFFF",
      borderTop: isFirst
        ? "1px solid #9d9d9d"
        : isEven
          ? "none"
          : "1px solid #eef2f6",
      borderBottom: !isEven ? "1px solid #eef2f6" : "none",
    };

    let attributeName = item;
    let sources = [];

    if (typeof item === "object") {
      attributeName = item.name;
      sources = item?.sources
        ? item.sources.map((source) => ({ source: source, snippet: "" }))
        : [];
    }

    return (
      <ListItem disablePadding style={style} key={index}>
        <ListItemButton
          className={classes.list_btn}
          sx={{
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <MUIToolTip
            additionalText={`${discoveryCtx.category} ${discoveryCtx.entity} has ${attributeName}`}
            llmSources={sources}
            title={``}
            showSnippets
            disableAction
            showSources
            tooltip={attributeName}
            llm={discoveryCtx.llm}
          >
            <ListItemText
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: 12,
                  color: "#1B2559",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  overflow: "hidden",
                  maxWidth: 240,
                  textOverflow: "ellipsis",
                },
                display: "flex",
                justifyContent: "center",
              }}
              primary={attributeName}
            />
          </MUIToolTip>
        </ListItemButton>
      </ListItem>
    );
  };

  if (Array.isArray(brandData) && brandData.length > 0) {
    const mainBrand = brandData.find(
      (brand) => brand.name === discoveryCtx.entity
    );

    if (mainBrand) {
      positiveAttributes = mainBrand?.positives;
      negativeAttributes = mainBrand?.negatives;
      loading = false;
    }
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }

  return (
    <MUICard
      title={cardTitle}
      className={classes.card}
      titleClassName={classes.title}
      variant="elevated"
      elevation={0}
    >
      {loading && !hasError && <LoadingSkeleton />}
      {!loading && !hasError && (
        <div className={classes.table_container}>
          <MUIList
            className={classes.list}
            title="Positive"
            data={positiveAttributes}
            render={renderRow}
          />
          <MUIList
            className={classes.list}
            title="Negative"
            data={negativeAttributes}
            render={renderRow}
          />
        </div>
      )}
      {hasError && (
        <div className={classes.error_view}>
          <ErrorView
            title={`Error Fetching ${discoveryType === "brand" ? ENTITY_LABELS.brandAttributes : ENTITY_LABELS.productAttributes}`}
            message={"Request Failed"}
          />
        </div>
      )}

      {/* Base Responses Modal */}
      <BaseResponsesModal
        open={infoModalOpen}
        title={infoModalTitle}
        content={infoModalContent}
        onClose={closeInfoModal}
      />
    </MUICard>
  );
}